import React, { useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Box from "@redbubble/design-system/react/Box";
import Text from "@redbubble/design-system/react/Text";
import styles from "./CommentsSection.scss";
import messages from "./messages";
import NewComment from "./NewComment";
import Comment from "./Comment";
import { commentsShape } from "../../../../../containers/apollo/useComments";

const Comments = ({
  artistId,
  avatar,
  comments,
  commentsCount,
  displayName,
  isArtist,
  locale,
  logEvent,
  userId,
  workId,
}) => {
  const [replyToCommentId, setReplyToCommentId] = useState(null);

  // eslint-disable-next-line react/prop-types
  const NewReply = ({ parentId, repliesCount }) => {
    return (
      <Box paddingLeft="s" paddingRight="s">
        <Box className={styles.replyThreadLine} />
        <NewComment
          avatar={avatar}
          commentsCount={commentsCount}
          displayName={displayName}
          isArtist={isArtist}
          logEvent={logEvent}
          parentId={parentId}
          repliesCount={repliesCount}
          setReplyToCommentId={setReplyToCommentId}
          userAccountLoading={false}
          userId={userId}
          userInfoLoading={false}
          workId={workId}
        />
      </Box>
    );
  };

  const ReplyButton = ({ id }) => {
    return (
      <Box
        paddingLeft="l"
        paddingTop="xs"
        display="inline-block"
        style={{ cursor: "pointer" }}
        onClick={() => {
          setReplyToCommentId(id);
        }}
      >
        <Text type="display6">
          <FormattedMessage {...messages.replyLink} />
        </Text>
      </Box>
    );
  };

  const Replies = ({ commentReplies }) => {
    return commentReplies.map(
      ({ comment, createdAt, id, user, canDelete, canReport }) => {
        return (
          <Box paddingLeft="s" paddingRight="s" key={`reply-${id}`}>
            <Box className={styles.replyThreadLine} />
            <Comment
              locale={locale}
              artistId={artistId}
              comment={comment}
              createdAt={createdAt}
              user={user}
              workId={workId}
              canDelete={canDelete}
              canReport={canReport}
            />
          </Box>
        );
      }
    );
  };

  return (
    <Box className={styles.commentsContainer}>
      {comments.map(
        ({
          comment,
          commentReplies,
          createdAt,
          id,
          user,
          canDelete,
          canReport,
        }) => {
          const hasReplies =
            Array.isArray(commentReplies) && commentReplies.length !== 0;
          return (
            <Box
              marginBottom={replyToCommentId === id ? 0 : 1.5}
              key={`comment-${id}`}
            >
              <Comment
                locale={locale}
                artistId={artistId}
                comment={comment}
                createdAt={createdAt}
                user={user}
                workId={workId}
                commentId={id}
                canDelete={canDelete}
                canReport={canReport}
              />
              {isArtist && <ReplyButton id={id} />}
              {hasReplies && <Replies commentReplies={commentReplies} />}
              {replyToCommentId === id && (
                <NewReply parentId={id} repliesCount={commentReplies.length} />
              )}
            </Box>
          );
        }
      )}
    </Box>
  );
};

Comments.propTypes = {
  artistId: PropTypes.string.isRequired,
  avatar: PropTypes.string,
  comments: commentsShape,
  commentsCount: PropTypes.number,
  displayName: PropTypes.string,
  isArtist: PropTypes.bool,
  locale: PropTypes.string,
  logEvent: PropTypes.func,
  userId: PropTypes.string,
  workId: PropTypes.string.isRequired,
};

Comments.defaultProps = {
  avatar: "",
  comments: [],
  commentsCount: 0,
  displayName: "",
  isArtist: false,
  locale: "en",
  logEvent: () => {},
  userId: "",
};

export default Comments;
